import { useParams, Navigate, Link } from "react-router-dom";
import {
  ArrowRight, ArrowUpRight, Check, Layers, Settings2, Building2, Sparkles, MapPin,
} from "lucide-react";
import Seo, { breadcrumbSchema, faqSchema, serviceSchema } from "@/components/Seo";
import { Reveal, Stagger, StaggerItem } from "@/components/Reveal";
import { Btn, Overline } from "@/components/Btn";
import { Breadcrumb } from "@/components/Breadcrumb";
import { CtaBand } from "@/components/CtaBand";
import { getService } from "@/data/services";
import { getLocale } from "@/data/locales";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";

const Chips = ({ title, items, icon: Icon }) => (
  <div className="rounded-xl border border-white/10 bg-ink-800 p-7">
    <div className="flex items-center gap-3 mb-5">
      <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-signature/15 text-signature"><Icon className="h-5 w-5" /></div>
      <h3 className="text-lg font-semibold text-white">{title}</h3>
    </div>
    <ul className="flex flex-wrap gap-2">
      {items.map((it) => (
        <li key={it} className="rounded-full border border-white/10 bg-ink-700 px-3.5 py-1.5 text-sm text-slate-300">{it}</li>
      ))}
    </ul>
  </div>
);

export default function ServicePage() {
  const { slug } = useParams();
  const s = getService(slug);
  if (!s) return <Navigate to="/404" replace />;

  const path = `/${s.slug}/`;
  const crumbs = [
    { label: "Accueil", to: "/" },
    { label: s.category, to: s.category === "Communication visuelle" ? "/communication-visuelle/" : "/enseigne-lumineuse/" },
    { label: s.nav, to: path },
  ];

  return (
    <>
      <Seo
        title={s.title}
        description={s.metaDescription}
        path={path}
        image={s.image}
        jsonLd={[
          serviceSchema(s.nav, s.metaDescription, path),
          breadcrumbSchema(crumbs),
          faqSchema(s.faq),
        ]}
      />

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-white/10 bg-ink-900" data-testid="service-hero">
        <div className="absolute inset-0 z-0">
          <img src={s.image} alt={s.h1} className="h-full w-full object-cover opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-t from-ink-900 via-ink-900/80 to-ink-900/55" />
          <div className="absolute inset-0 bg-grid bg-grid-fade opacity-30" />
        </div>
        <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-10 lg:py-16">
          <Breadcrumb items={crumbs} />
          <Reveal className="mt-8 max-w-3xl">
            <Overline>{s.overline}</Overline>
            <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-extrabold uppercase tracking-tight text-white leading-[0.98]">{s.h1}</h1>
            {s.intro.map((p, i) => (
              <p key={i} className="mt-5 text-base sm:text-lg text-slate-300 leading-relaxed">{p}</p>
            ))}
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Btn to="/contact/" size="lg" data-testid="service-devis-btn">Demander un devis <ArrowRight className="h-4 w-4" /></Btn>
              <Btn to="/realisations/" variant="outline" size="lg">Voir les réalisations</Btn>
            </div>
            {s.highlights && (
              <div className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-sm text-chrome-mid">
                {s.highlights.map((h) => (
                  <span key={h} className="flex items-center gap-2"><Check className="h-4 w-4 text-signature" /> {h}</span>
                ))}
              </div>
            )}
          </Reveal>
        </div>
      </section>

      {/* Sections */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          <div className="lg:col-span-2 space-y-12">
            {s.sections.map((sec, i) => (
              <Reveal key={i} className="scroll-mt-28">
                <h2 className="text-2xl sm:text-3xl font-bold text-white">{sec.h2}</h2>
                <p className="mt-4 text-slate-300 leading-relaxed">{sec.body}</p>
                {sec.bullets && (
                  <ul className="mt-5 space-y-2.5">
                    {sec.bullets.map((b) => (
                      <li key={b} className="flex items-start gap-3 text-slate-300">
                        <Check className="h-5 w-5 text-signature shrink-0 mt-0.5" /> {b}
                      </li>
                    ))}
                  </ul>
                )}
              </Reveal>
            ))}

            <Reveal className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <Chips title="Matériaux" items={s.materials} icon={Layers} />
              <Chips title="Options" items={s.options} icon={Settings2} />
            </Reveal>

            <Reveal>
              <h2 className="text-2xl sm:text-3xl font-bold text-white flex items-center gap-3"><Building2 className="h-6 w-6 text-signature" /> Applications professionnelles</h2>
              <div className="mt-5 flex flex-wrap gap-2">
                {s.applications.map((a) => (
                  <span key={a} className="rounded-lg border border-white/10 bg-ink-800 px-4 py-2 text-sm text-slate-300">{a}</span>
                ))}
              </div>
            </Reveal>

            <Reveal>
              <h2 className="text-2xl sm:text-3xl font-bold text-white flex items-center gap-3"><Sparkles className="h-6 w-6 text-signature" /> Avantages</h2>
              <ul className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
                {s.advantages.map((a) => (
                  <li key={a} className="flex items-start gap-3 rounded-lg border border-white/10 bg-ink-800 p-4 text-sm text-slate-300">
                    <Check className="h-5 w-5 text-signature shrink-0" /> {a}
                  </li>
                ))}
              </ul>
            </Reveal>

            {/* FAQ */}
            <Reveal>
              <h2 className="text-2xl sm:text-3xl font-bold text-white mb-5">Questions fréquentes</h2>
              <Accordion type="single" collapsible className="space-y-3">
                {s.faq.map((f, i) => (
                  <AccordionItem key={i} value={`f-${i}`} className="rounded-xl border border-white/10 bg-ink-800 px-5" data-testid={`service-faq-${i}`}>
                    <AccordionTrigger className="text-left text-white hover:no-underline py-5">{f.q}</AccordionTrigger>
                    <AccordionContent className="text-slate-400 pb-5">{f.a}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </Reveal>
          </div>

          {/* Sidebar */}
          <aside className="lg:col-span-1">
            <div className="lg:sticky lg:top-28 space-y-6">
              <div className="rounded-2xl border border-signature/30 bg-gradient-to-b from-ink-800 to-ink-900 p-7 glow-red">
                <h3 className="text-xl font-bold text-white">Votre projet d'enseigne</h3>
                <p className="mt-3 text-sm text-slate-300">Étude, fabrication et pose par un seul interlocuteur. Devis gratuit et sans engagement.</p>
                <Btn to="/contact/" className="mt-5 w-full" data-testid="sidebar-devis-btn">Demander un devis <ArrowRight className="h-4 w-4" /></Btn>
              </div>

              <div className="rounded-2xl border border-white/10 bg-ink-800 p-7">
                <p className="text-xs font-mono uppercase tracking-wider text-signature mb-4">Services complémentaires</p>
                <ul className="space-y-1">
                  {s.related.map((r) => {
                    const rs = getService(r);
                    if (!rs) return null;
                    return (
                      <li key={r}>
                        <Link to={`/${rs.slug}/`} className="group flex items-center justify-between rounded-md px-3 py-2 text-sm text-slate-300 hover:text-white hover:bg-white/5 transition-colors">
                          {rs.nav} <ArrowUpRight className="h-4 w-4 text-chrome-dark group-hover:text-signature" />
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </div>

              {s.relatedLocales && (
                <div className="rounded-2xl border border-white/10 bg-ink-800 p-7">
                  <p className="text-xs font-mono uppercase tracking-wider text-signature mb-4">Zones d'intervention</p>
                  <ul className="space-y-1">
                    {s.relatedLocales.map((l) => {
                      const loc = getLocale(l);
                      if (!loc) return null;
                      return (
                        <li key={l}>
                          <Link to={`/${loc.slug}/`} className="group flex items-center gap-2 rounded-md px-3 py-2 text-sm text-slate-300 hover:text-white hover:bg-white/5 transition-colors">
                            <MapPin className="h-4 w-4 text-chrome-dark group-hover:text-signature" /> {loc.nav}
                          </Link>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
            </div>
          </aside>
        </div>
      </section>

      <CtaBand />
    </>
  );
}
